/**
 * audio.ts -- the bell system's audio engine.
 *
 * Standalone: no framework, no dependencies, no imports from other src files.
 *
 * Target hardware is eleven wall-mounted Samsung Galaxy Tab A9+ tablets
 * (Android 13, Chrome/WebView, kiosk mode) that must ring a bell on time,
 * every period, all day, from the tablet's own speaker. Silence is a total
 * product failure, so this module is written defensively:
 *
 *   - Both sounds are fetched and DECODED UP FRONT and held in memory, so
 *     ringing a bell never waits on the network or on a decoder.
 *   - The raw encoded bytes are kept too, so if the OS ever tears the
 *     AudioContext down we can rebuild without touching the network.
 *   - A near-silent looping buffer keeps the output stream alive so Android
 *     does not idle the audio path out from under us.
 *   - Context state is re-checked on visibilitychange, on pageshow, on the
 *     context's own statechange event, and on a periodic timer. If it went
 *     suspended we try to resume; if we cannot, onDisarm fires so the UI can
 *     ask for the one tap that fixes it.
 *
 * ARMING / ANDROID AUTOPLAY POLICY -- read this before changing armFromGesture:
 *
 *   A fresh AudioContext starts 'suspended'. Chrome will only let it start if
 *   resume() is called while the browser still considers a user gesture to be
 *   "being handled". That window is the SYNCHRONOUS part of the event handler
 *   turn -- it is gone after the first await. So armFromGesture() calls
 *   resume() and starts a silent one-sample buffer BEFORE it awaits anything.
 *   Awaiting first (e.g. `await fetch(...)` then resume) silently fails on
 *   Android and you get a dead bell system that looked fine in desktop testing.
 */

export type BellSound = 'bell' | 'warn';

export interface AudioEngine {
  /** true once the AudioContext is running and both buffers are decoded */
  readonly armed: boolean;
  /** Try to arm WITHOUT a user gesture (call on page load). Resolves true if it worked. */
  tryArm(): Promise<boolean>;
  /** Call from INSIDE a real user-gesture handler (PIN tap / arm-overlay tap). */
  armFromGesture(): Promise<boolean>;
  /** Fire a sound now. No-op with a console warning if not armed. */
  play(sound: BellSound): void;
  /** Stop anything currently sounding. */
  stop(): void;
  /** 0..1, persisted to localStorage. */
  readonly volume: number;
  setVolume(v: number): void;
  /** Called when the context gets suspended/interrupted so the UI can show a re-arm prompt. */
  onDisarm(cb: () => void): void;
  /** Called when the context recovers on its own. */
  onRearm(cb: () => void): void;
}

/** localStorage key for the classroom volume setting. */
const VOLUME_STORAGE_KEY = 'valor.bells.volume';

/** Classrooms are loud and the tablet speaker is small: default to full. */
const DEFAULT_VOLUME = 1.0;

/** Where the precached audio lives. mp3 first, ogg as the fallback. */
const SOURCES: Record<BellSound, readonly string[]> = {
  bell: ['/audio/bell.mp3', '/audio/bell.ogg'],
  warn: ['/audio/warn.mp3', '/audio/warn.ogg'],
};

const ALL_SOUNDS: readonly BellSound[] = ['bell', 'warn'];

/** How often to re-check that the context is still alive (ms). */
const HEALTH_INTERVAL_MS = 15_000;

/** Retry backoff for the initial load if the network is not up yet (ms). */
const LOAD_RETRY_MS = [1_000, 3_000, 8_000, 20_000, 60_000];

/**
 * How long the looping keep-alive buffer is. Its CONTENT is digital silence —
 * see startKeepAlive() for why it used to be noise and why it is not any more.
 */
const KEEPALIVE_SECONDS = 2;

/**
 * How far a still-sounding warn is ducked when the bell rings over it.
 *
 * This number is a headroom calculation, not a taste decision. Both files are
 * mastered to about -1.2 dBFS (bell peak 0.872, warn peak 0.829), and the
 * summed graph hard-clips at the output if it ever exceeds 1.0. The largest
 * duck that still cannot clip is (1.0 - 0.872) / 0.829 = 0.148. 0.10 is used
 * for margin. Measured by rendering the pair offline at the warning's six
 * loudest instants: worst case -0.92 dBFS, nothing over full scale.
 */
const WARN_DUCK_GAIN = 0.10;

/**
 * How fast that duck is applied -- and this is the part that actually matters.
 *
 * The bell reaches its absolute peak 9.9 ms after it starts. Duck the warning
 * over a comfortable-sounding 150 ms and the warning is still at nearly full
 * level throughout the bell's attack, so the two sum straight through the
 * ceiling: rendered offline with the bell landing on the warning's loudest
 * instant, that combination peaks at +2.33 dBFS with 102 samples clipped.
 *
 * So this must stay WELL BELOW that 9.9 ms. At 5 ms the worst measured case
 * across the warning's loudest instants is -0.92 dBFS -- effectively the
 * bell's own peak, i.e. the warning stops contributing to the ceiling at all.
 * Still long enough (220 samples) to be click-free on a sustained tone.
 */
const WARN_DUCK_RAMP_S = 0.005;

/**
 * Fade applied to a sound that is being re-triggered while still sounding.
 *
 * Deliberately SHORTER than the bell's own 9.9 ms attack. The old copy is
 * therefore always decaying faster than the new copy is rising, which bounds
 * their sum by a single bell's peak -- so double-tapping "ring bell" cannot
 * clip. Lengthening this reintroduces that clip.
 */
const RETRIGGER_FADE_S = 0.008;

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

interface ActivePlayback {
  sound: BellSound;
  source: AudioBufferSourceNode;
  gain: GainNode;
}

class WebAudioEngine implements AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;

  /** Decoded, ready to play instantly. */
  private buffers: Partial<Record<BellSound, AudioBuffer>> = {};
  /** Encoded bytes, kept so we can re-decode without the network. */
  private encoded: Partial<Record<BellSound, Uint8Array>> = {};

  private loadPromise: Promise<boolean> | null = null;
  private loadAttempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  private keepAlive: AudioBufferSourceNode | null = null;
  private active: ActivePlayback[] = [];

  private vol: number = DEFAULT_VOLUME;
  private lastArmed = false;

  private disarmCbs: Array<() => void> = [];
  private rearmCbs: Array<() => void> = [];

  constructor() {
    this.vol = this.loadVolume();
    // Create the context immediately (it will be 'suspended', which is fine
    // and expected) so decoding can start right now rather than waiting for a
    // gesture. By the time a teacher taps, the bell is already in memory.
    this.ensureContext();
    void this.ensureBuffers();
    this.installLifecycleHooks();
  }

  // ------------------------------------------------------------------ state

  get armed(): boolean {
    return this.ctx !== null
      && this.ctx.state === 'running'
      && this.buffersReady();
  }

  get volume(): number {
    return this.vol;
  }

  private buffersReady(): boolean {
    return ALL_SOUNDS.every((s) => this.buffers[s] != null);
  }

  // ------------------------------------------------------------- arming

  /**
   * Attempt to arm with no user gesture. Some kiosk builds (and Chrome with
   * autoplay explicitly allowed for the origin) permit this; most do not.
   * Reports honestly by checking the resulting STATE, not merely whether
   * resume() resolved -- outside a gesture Chrome resolves the promise while
   * leaving the context suspended.
   */
  async tryArm(): Promise<boolean> {
    const ctx = this.ensureContext();
    if (!ctx) return false;

    try {
      await ctx.resume();
    } catch {
      /* expected without a gesture */
    }
    await this.ensureBuffers();

    if (ctx.state === 'running') {
      this.startKeepAlive();
    }
    this.syncArmedState();
    return this.armed;
  }

  /**
   * Arm from inside a real user-gesture handler.
   *
   * Everything Android requires to happen "during" the gesture -- resume()
   * and an actual buffer playback -- is issued synchronously, BEFORE the
   * first await. Do not reorder this: moving an await above them is exactly
   * the bug that produces a silent bell system on the tablets while still
   * working on a desktop browser.
   */
  async armFromGesture(): Promise<boolean> {
    const ctx = this.ensureContext();
    if (!ctx) return false;

    // --- synchronous gesture window: no awaits above this line ---
    const resuming = ctx.resume().catch(() => undefined);
    this.pokeSilentBuffer(ctx);
    // --- end of the part that must stay synchronous ---

    await resuming;
    await this.ensureBuffers();

    if (ctx.state === 'running') {
      this.startKeepAlive();
    } else {
      console.warn(
        '[audio] armFromGesture: context is still "%s" after resume(). '
        + 'The tap may not have counted as a user gesture.',
        ctx.state,
      );
    }
    this.syncArmedState();
    return this.armed;
  }

  /**
   * Play one sample of silence. Android treats "a buffer was actually
   * started during the gesture" as the thing that unlocks output; calling
   * resume() alone is not always enough.
   */
  private pokeSilentBuffer(ctx: AudioContext): void {
    try {
      const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
    } catch (err) {
      console.warn('[audio] silent unlock buffer failed', err);
    }
  }

  // ------------------------------------------------------------- playback

  play(sound: BellSound): void {
    const ctx = this.ctx;
    const master = this.master;

    if (!ctx || !master) {
      console.warn('[audio] play(%s) ignored: no AudioContext', sound);
      return;
    }
    const buffer = this.buffers[sound];
    if (!buffer) {
      console.warn(
        '[audio] play(%s) ignored: buffer not decoded yet (still loading or load failed)',
        sound,
      );
      void this.ensureBuffers();
      return;
    }
    if (ctx.state !== 'running') {
      console.warn(
        '[audio] play(%s) ignored: AudioContext is "%s", not armed',
        sound, ctx.state,
      );
      // Nudge it -- if this succeeds the next bell will be fine.
      void this.checkHealth();
      return;
    }

    // Re-triggering the same sound restarts it rather than layering a second
    // copy on top of itself (which would just sound like distortion).
    this.fadeOutSound(sound, RETRIGGER_FADE_S);

    // A bell fired while the warning is still ringing must be the sound that
    // wins, but cutting the warning dead is jarring and summing both at full
    // level would clip. Duck the warning under the bell instead.
    if (sound === 'bell') {
      this.duckSound('warn', WARN_DUCK_GAIN, WARN_DUCK_RAMP_S);
    }

    const gain = ctx.createGain();
    gain.gain.value = 1;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);
    gain.connect(master);

    const entry: ActivePlayback = { sound, source, gain };
    source.onended = () => {
      this.active = this.active.filter((a) => a !== entry);
      try {
        gain.disconnect();
      } catch {
        /* already torn down */
      }
    };
    this.active.push(entry);

    try {
      source.start();
    } catch (err) {
      console.error('[audio] failed to start %s', sound, err);
      this.active = this.active.filter((a) => a !== entry);
    }
  }

  stop(): void {
    const ctx = this.ctx;
    const now = ctx ? ctx.currentTime : 0;
    for (const entry of this.active.slice()) {
      try {
        entry.gain.gain.cancelScheduledValues(now);
        entry.gain.gain.setValueAtTime(entry.gain.gain.value, now);
        entry.gain.gain.linearRampToValueAtTime(0, now + 0.02);
        entry.source.stop(now + 0.03);
      } catch {
        try {
          entry.source.stop();
        } catch {
          /* already stopped */
        }
      }
    }
    this.active = [];
  }

  private fadeOutSound(sound: BellSound, seconds: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    for (const entry of this.active.filter((a) => a.sound === sound)) {
      try {
        entry.gain.gain.cancelScheduledValues(now);
        entry.gain.gain.setValueAtTime(entry.gain.gain.value, now);
        entry.gain.gain.linearRampToValueAtTime(0, now + seconds);
        entry.source.stop(now + seconds + 0.01);
      } catch {
        /* fine */
      }
    }
  }

  private duckSound(sound: BellSound, to: number, seconds: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    for (const entry of this.active.filter((a) => a.sound === sound)) {
      try {
        entry.gain.gain.cancelScheduledValues(now);
        entry.gain.gain.setValueAtTime(entry.gain.gain.value, now);
        entry.gain.gain.linearRampToValueAtTime(to, now + seconds);
      } catch {
        /* fine */
      }
    }
  }

  // --------------------------------------------------------------- volume

  setVolume(v: number): void {
    const clamped = Math.max(0, Math.min(1, Number.isFinite(v) ? v : DEFAULT_VOLUME));
    this.vol = clamped;
    this.applyVolume();
    try {
      window.localStorage.setItem(VOLUME_STORAGE_KEY, String(clamped));
    } catch {
      /* kiosk may block storage; volume still applies for this session */
    }
  }

  private applyVolume(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    try {
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      // Short ramp instead of a jump, to avoid a zipper click.
      master.gain.linearRampToValueAtTime(this.vol, now + 0.05);
    } catch {
      master.gain.value = this.vol;
    }
  }

  private loadVolume(): number {
    try {
      const raw = window.localStorage.getItem(VOLUME_STORAGE_KEY);
      if (raw === null) return DEFAULT_VOLUME;
      const n = Number.parseFloat(raw);
      if (!Number.isFinite(n)) return DEFAULT_VOLUME;
      return Math.max(0, Math.min(1, n));
    } catch {
      return DEFAULT_VOLUME;
    }
  }

  // -------------------------------------------------------------- context

  private ensureContext(): AudioContext | null {
    if (this.ctx && this.ctx.state !== 'closed') return this.ctx;

    const Ctor = getAudioContextCtor();
    if (!Ctor) {
      console.error('[audio] Web Audio API is unavailable in this browser');
      return null;
    }

    const wasClosed = this.ctx?.state === 'closed';
    if (wasClosed) {
      console.warn('[audio] AudioContext was closed; rebuilding');
      this.buffers = {};
      this.keepAlive = null;
      this.active = [];
      this.loadPromise = null;
    }

    try {
      const ctx = new Ctor({ latencyHint: 'interactive' });
      const master = ctx.createGain();
      master.gain.value = this.vol;
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;

      ctx.addEventListener?.('statechange', this.onStateChange);

      if (wasClosed) {
        // Re-decode from the bytes we kept; no network needed.
        void this.ensureBuffers();
      }
      return ctx;
    } catch (err) {
      console.error('[audio] could not create AudioContext', err);
      return null;
    }
  }

  private onStateChange = (): void => {
    const state = this.ctx?.state;
    if (state === 'running') {
      this.startKeepAlive();
    }
    this.syncArmedState();
  };

  /**
   * Hold the output stream open so Android does not idle the audio path out
   * from under a bell that is still four minutes away.
   *
   * The buffer is SILENT, and that is a correction rather than an oversight.
   * It used to be white noise at 1e-4 — about -80 dBFS — on the theory that a
   * genuinely non-zero signal is harder for the platform to optimise away. The
   * theory was never tested against a teardown that actually happened; it was
   * written defensively on day one. What did happen is that a tablet hung in
   * 2026 played it audibly: HANG.md sends every tablet out with the media
   * volume near maximum so the bell carries over a room of children, and at
   * that gain -80 dBFS of broadband hiss sits right at the amplifier's own
   * noise floor. It is also wired past the master gain, so the classroom volume
   * control could not turn it down. A room of children listening to hiss all
   * day is a real defect; the teardown it guarded against is a hypothesis.
   *
   * What actually keeps the stream alive is this node PLAYING, not what is in
   * it — the context stays 'running' either way, and `createBuffer` hands back
   * a zero-filled buffer, so silence costs nothing and needs no fill loop.
   *
   * If a tablet is ever found silent at a bell with the context still claiming
   * to be 'running', this is the first place to look — but fix it with
   * something inaudible at full volume, not by putting the noise back. The
   * 15-second health check below is the real safety net, and it fires onDisarm
   * so the wall asks for the one tap that fixes it.
   */
  private startKeepAlive(): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running' || this.keepAlive) return;
    try {
      const len = Math.max(1, Math.floor(ctx.sampleRate * KEEPALIVE_SECONDS));
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      // Deliberately NOT routed through the master gain: muting the classroom
      // volume must not also switch off the thing keeping the stream alive.
      src.connect(ctx.destination);
      src.start();
      this.keepAlive = src;
    } catch (err) {
      console.warn('[audio] keep-alive could not start', err);
    }
  }

  // ---------------------------------------------------------------- loading

  private ensureBuffers(): Promise<boolean> {
    if (this.buffersReady()) return Promise.resolve(true);
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = this.loadAll().finally(() => {
      this.loadPromise = null;
    });
    return this.loadPromise;
  }

  private async loadAll(): Promise<boolean> {
    const ctx = this.ensureContext();
    if (!ctx) return false;

    await Promise.all(ALL_SOUNDS.map((s) => this.loadOne(ctx, s)));

    const ok = this.buffersReady();
    if (!ok) {
      this.scheduleRetry();
    } else {
      this.loadAttempt = 0;
    }
    this.syncArmedState();
    return ok;
  }

  private async loadOne(ctx: AudioContext, sound: BellSound): Promise<void> {
    if (this.buffers[sound]) return;

    // If we already have the bytes (e.g. the context was rebuilt), just decode.
    const cached = this.encoded[sound];
    if (cached) {
      const buf = await this.decode(ctx, cached, sound);
      if (buf) {
        this.buffers[sound] = buf;
        return;
      }
    }

    for (const url of SOURCES[sound]) {
      let bytes: Uint8Array;
      try {
        const res = await fetch(url, { cache: 'force-cache' });
        if (!res.ok) {
          console.error('[audio] fetch %s -> HTTP %d', url, res.status);
          continue;
        }
        bytes = new Uint8Array(await res.arrayBuffer());
      } catch (err) {
        console.error('[audio] fetch %s failed', url, err);
        continue;
      }

      const buf = await this.decode(ctx, bytes, sound);
      if (buf) {
        this.encoded[sound] = bytes;
        this.buffers[sound] = buf;
        return;
      }
      console.error('[audio] %s decoded to nothing; trying next format', url);
    }

    console.error(
      '[audio] COULD NOT LOAD "%s" from any of: %s -- the bell will not sound',
      sound, SOURCES[sound].join(', '),
    );
  }

  private async decode(
    ctx: AudioContext,
    bytes: Uint8Array,
    sound: BellSound,
  ): Promise<AudioBuffer | null> {
    // decodeAudioData detaches the ArrayBuffer it is given, so always hand it
    // a fresh copy -- otherwise the cached bytes are destroyed by the first
    // decode and a later context rebuild has nothing to work from.
    const copy = bytes.slice().buffer as ArrayBuffer;
    try {
      const decoded = await new Promise<AudioBuffer>((resolve, reject) => {
        const maybe = ctx.decodeAudioData(copy, resolve, reject);
        // Modern Chromium returns a promise; older WebViews only take callbacks.
        if (maybe && typeof maybe.then === 'function') {
          maybe.then(resolve, reject);
        }
      });
      if (!decoded || decoded.length === 0) return null;
      return decoded;
    } catch (err) {
      console.error('[audio] decodeAudioData failed for "%s"', sound, err);
      return null;
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer) return;
    const delay = LOAD_RETRY_MS[Math.min(this.loadAttempt, LOAD_RETRY_MS.length - 1)];
    this.loadAttempt++;
    console.warn('[audio] retrying sound load in %dms', delay);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.ensureBuffers();
    }, delay);
  }

  // ------------------------------------------------------------ lifecycle

  /**
   * The engine is intentionally a page-lifetime object: on a kiosk tablet the
   * page is loaded once and left running for the whole school day, so these
   * hooks are never torn down. There is deliberately no dispose() -- if one is
   * ever needed, it must remove these listeners and clear this interval.
   */
  private installLifecycleHooks(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('focus', this.onVisibility);
    }
    setInterval(() => {
      void this.checkHealth();
    }, HEALTH_INTERVAL_MS);
  }

  private onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.hidden) return;
    void this.checkHealth();
  };

  private onPageShow = (): void => {
    void this.checkHealth();
  };

  /**
   * The all-day watchdog. Android will happily suspend an AudioContext when
   * the screen sleeps, when another app grabs audio focus, or seemingly just
   * because. If we can resume without a gesture we do so silently; if we
   * cannot, onDisarm fires and the UI asks for one tap.
   */
  private async checkHealth(): Promise<void> {


    const ctx = this.ensureContext();
    if (!ctx) {
      this.syncArmedState();
      return;
    }

    if (!this.buffersReady() && !this.loadPromise && !this.retryTimer) {
      void this.ensureBuffers();
    }

    if (ctx.state !== 'running') {
      try {
        await ctx.resume();
      } catch {
        /* needs a gesture */
      }
    }

    if (ctx.state === 'running') {
      this.startKeepAlive();
    }
    this.syncArmedState();
  }

  /** Fire onDisarm / onRearm only on an actual transition. */
  private syncArmedState(): void {
    const now = this.armed;
    if (now === this.lastArmed) return;
    this.lastArmed = now;

    const cbs = now ? this.rearmCbs : this.disarmCbs;
    if (!now) {
      console.warn(
        '[audio] DISARMED (context "%s", buffers %s) -- a tap is needed',
        this.ctx?.state ?? 'none',
        this.buffersReady() ? 'ready' : 'not ready',
      );
    }
    for (const cb of cbs.slice()) {
      try {
        cb();
      } catch (err) {
        console.error('[audio] listener threw', err);
      }
    }
  }

  onDisarm(cb: () => void): void {
    this.disarmCbs.push(cb);
  }

  onRearm(cb: () => void): void {
    this.rearmCbs.push(cb);
  }
}

export function createAudioEngine(): AudioEngine {
  return new WebAudioEngine();
}
